import { create } from 'zustand';

export type SiteVisitRecordingPhase = 'idle' | 'recording' | 'paused';

interface SiteVisitRecordingStore {
	phase: SiteVisitRecordingPhase;
	displayTime: string;
	recordingBytesTotal: number;
	idbChunkCount: number;
	browserStorageUsed: number | null;
	browserStorageQuota: number | null;
	lastRecordingPlaybackUrl: string | null;
	/** Site id for the active or last-completed session (set when recording starts). */
	sessionSiteId: number | null;
	/** When true, Stop uploads to the backend immediately (post–visit success page). */
	uploadOnStop: boolean;
	isSaving: boolean;
	uploadQueueIds: number[];
	setPhase: (phase: SiteVisitRecordingPhase) => void;
	setDisplayTime: (displayTime: string) => void;
	setRecordingBytesTotal: (bytes: number) => void;
	setIdbChunkCount: (count: number) => void;
	setBrowserStorageUsed: (bytes: number | null) => void;
	setBrowserStorageQuota: (bytes: number | null) => void;
	setLastRecordingPlaybackUrl: (url: string | null) => void;
	setSessionSiteId: (siteId: number | null) => void;
	setUploadOnStop: (uploadOnStop: boolean) => void;
	setIsSaving: (isSaving: boolean) => void;
	setUploadQueueIds: (ids: number[]) => void;
	resetUiState: () => void;
	pausedFormEditNonce: number;
	notifyPausedFormEdit: () => void;
	resetPausedFormEdit: () => void;
}

export const useSiteVisitRecordingStore = create<SiteVisitRecordingStore>((set) => ({
	phase: 'idle',
	displayTime: '00:00:00',
	recordingBytesTotal: 0,
	idbChunkCount: 0,
	browserStorageUsed: null,
	browserStorageQuota: null,
	lastRecordingPlaybackUrl: null,
	sessionSiteId: null,
	uploadOnStop: false,
	isSaving: false,
	uploadQueueIds: [],
	setPhase: (phase): void => set((state) => (state.phase === phase ? state : { phase })),
	setDisplayTime: (displayTime): void => set((state) => (state.displayTime === displayTime ? state : { displayTime })),
	setRecordingBytesTotal: (recordingBytesTotal): void => set({ recordingBytesTotal }),
	setIdbChunkCount: (idbChunkCount): void => set({ idbChunkCount }),
	setBrowserStorageUsed: (browserStorageUsed): void => set({ browserStorageUsed }),
	setBrowserStorageQuota: (browserStorageQuota): void => set({ browserStorageQuota }),
	setLastRecordingPlaybackUrl: (lastRecordingPlaybackUrl): void => set({ lastRecordingPlaybackUrl }),
	setSessionSiteId: (sessionSiteId): void => set({ sessionSiteId }),
	setUploadOnStop: (uploadOnStop): void => set({ uploadOnStop }),
	setIsSaving: (isSaving): void => set({ isSaving }),
	setUploadQueueIds: (uploadQueueIds): void => set({ uploadQueueIds }),
	pausedFormEditNonce: 0,
	notifyPausedFormEdit: (): void =>
		set((state) => (state.phase === 'paused' ? { pausedFormEditNonce: state.pausedFormEditNonce + 1 } : state)),
	resetPausedFormEdit: (): void => set({ pausedFormEditNonce: 0 }),
	resetUiState: (): void =>
		set({
			phase: 'idle',
			displayTime: '00:00:00',
			recordingBytesTotal: 0,
			idbChunkCount: 0,
			browserStorageUsed: null,
			browserStorageQuota: null,
			sessionSiteId: null,
			uploadOnStop: false,
			isSaving: false,
			uploadQueueIds: [],
			pausedFormEditNonce: 0,
		}),
}));

export function isSiteVisitRecordingActive(phase: SiteVisitRecordingPhase): boolean {
	return phase === 'recording' || phase === 'paused';
}
